// src/pages/Lawyer/LawyerWorkloadDashboard.jsx
import React, { useMemo, useState, useCallback, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Badge,
  Spinner,
  Form,
  Table,
  ProgressBar,
  InputGroup,
} from "react-bootstrap";
import Swal from "sweetalert2";
import SelectLawyer from "../../components/Common/SelectLawyer";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { pdf } from "@react-pdf/renderer";
import LawyerWorkloadDashboardPDF from "./LawyerWorkloadDashboardPDF";

import { getLawyerWorkloadDashboard } from "../../services/caseDashboardService";

import "./LawyerWorkloadDashboard.css";

// ==========================
// Doughnut: Center text plugin (Total + label)
// ==========================
const doughnutCenterTextPlugin = {
  id: "doughnutCenterTextPlugin",
  afterDraw(chart, args, pluginOptions) {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;

    const opts = pluginOptions || {};
    const textTop = opts.textTop ?? "";
    const textBottom = opts.textBottom ?? "";
    const colorTop = opts.colorTop ?? "#111827";
    const colorBottom = opts.colorBottom ?? "#6b7280";

    const fontTop =
      opts.fontTop ??
      "800 28px Inter, system-ui, -apple-system, Segoe UI, Roboto";
    const fontBottom =
      opts.fontBottom ??
      "600 12px Inter, system-ui, -apple-system, Segoe UI, Roboto";

    const centerX = (chartArea.left + chartArea.right) / 2;
    const centerY = (chartArea.top + chartArea.bottom) / 2;

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    if (String(textTop).length) {
      ctx.fillStyle = colorTop;
      ctx.font = fontTop;
      ctx.fillText(String(textTop), centerX, centerY - 6);
    }

    if (String(textBottom).length) {
      ctx.fillStyle = colorBottom;
      ctx.font = fontBottom;
      ctx.fillText(String(textBottom), centerX, centerY + 16);
    }

    ctx.restore();
  },
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Tooltip,
  Legend,
  Filler,
  doughnutCenterTextPlugin
);

// ==========================
// Helpers generales
// ==========================

const safeDate = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const fmtDDMMYYYY = (d) => {
  const dt = safeDate(d);
  if (!dt) return "—";
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const toISODate = (d) => {
  const dt = safeDate(d);
  if (!dt) return "";
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const subDaysISO = (days) => {
  const dt = new Date();
  dt.setDate(dt.getDate() - days);
  return toISODate(dt);
};

const subMonthsISO = (months) => {
  const dt = new Date();
  dt.setMonth(dt.getMonth() - months);
  return toISODate(dt);
};

const daysDiff = (from, to) => {
  const a = safeDate(from);
  const b = safeDate(to);
  if (!a || !b) return null;
  const ms = b.getTime() - a.getTime();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
};

const clampPct = (n) => Math.max(0, Math.min(100, n || 0));

const monthLabelES = (periodYYYYMM) => {
  if (!periodYYYYMM || typeof periodYYYYMM !== "string") return "—";
  const [y, m] = periodYYYYMM.split("-");
  const year = Number(y);
  const month = Number(m);
  if (!year || !month) return periodYYYYMM;

  const months = [
    "Ene",
    "Feb",
    "Mar",
    "Abr",
    "May",
    "Jun",
    "Jul",
    "Ago",
    "Sep",
    "Oct",
    "Nov",
    "Dic",
  ];
  return `${months[month - 1]} ${year}`;
};

// ==========================
// Paleta + helpers RGBA
// ==========================
const COLORS = {
  opened: "#0d6efd", // primary
  closed: "#198754", // success
  bar: "#6610f2", // indigo
  danger: "#dc3545",
  warning: "#ffc107",
  info: "#0dcaf0",
};

const hexToRgba = (hex, alpha = 1) => {
  try {
    const h = hex.replace("#", "").trim();
    const full =
      h.length === 3
        ? h
            .split("")
            .map((c) => c + c)
            .join("")
        : h;
    const r = parseInt(full.substring(0, 2), 16);
    const g = parseInt(full.substring(2, 4), 16);
    const b = parseInt(full.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  } catch {
    return hex;
  }
};

const DONUT_COLORS = [
  "#0d6efd",
  "#198754",
  "#ffc107",
  "#dc3545",
  "#6f42c1",
  "#20c997",
  "#0dcaf0",
  "#fd7e14",
  "#6c757d",
  "#d63384",
  "#6610f2",
  "#198754",
];

// ==========================
// Dark mode detector (igual enfoque que SelectLawyer)
// ==========================
const checkDarkMode = () =>
  document.body.classList.contains("dark-mode") ||
  document.querySelector(".app")?.classList.contains("dark-mode");

// ==========================
// Componentes pequeños (UI)
// ==========================
const KPI = ({
  title,
  value,
  hint,
  badge,
  badgeVariant = "secondary",
  danger = false,
  accent = COLORS.opened,
  isDarkMode = false,
  iconClass = "bi bi-graph-up",
}) => {
  const bg = isDarkMode ? "#1e1e2f" : "#ffffff";
  const border = isDarkMode ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)";
  const shadow = isDarkMode
    ? "0 12px 28px rgba(0,0,0,0.35)"
    : "0 10px 24px rgba(0,0,0,0.10)";
  const muted = isDarkMode ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.60)";
  const text = isDarkMode ? "#ffffff" : "#111827";

  return (
    <div
      className="lwd-kpi-card h-100"
      style={{ background: bg, borderColor: border, boxShadow: shadow }}
    >
      <div className="lwd-kpi-accent" style={{ background: accent }} />
      <div className="lwd-kpi-body">
        <div className="d-flex justify-content-between align-items-start gap-2">
          <div className="d-flex align-items-center gap-2">
            <i className={iconClass} style={{ color: accent, fontSize: 18 }} />
            <div className="lwd-kpi-title" style={{ color: muted }}>
              {title}
            </div>
          </div>
          {badge ? <Badge bg={badgeVariant}>{badge}</Badge> : null}
        </div>

        <div
          className="lwd-kpi-value mt-2"
          style={{ color: danger ? COLORS.danger : text }}
        >
          {value}
        </div>

        {hint ? (
          <div className="small mt-1" style={{ color: muted }}>
            {hint}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const EmptyState = ({ title, subtitle, isDarkMode }) => (
  <div
    className="text-center py-4"
    style={{
      color: isDarkMode ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.55)",
    }}
  >
    <div className="fw-bold">{title}</div>
    {subtitle ? <div className="small mt-1">{subtitle}</div> : null}
  </div>
);

const QuickRangeButton = ({ active, children, onClick }) => (
  <Button
    variant={active ? "primary" : "outline-secondary"}
    onClick={onClick}
    className={active ? "fw-semibold" : ""}
  >
    {children}
  </Button>
);

const DeadlineBadge = ({
  isDeadlineBasedCase,
  responseDeadline,
  closingDate,
  isDarkMode,
}) => {
  if (!isDeadlineBasedCase) return <span style={{ opacity: 0.7 }}>—</span>;

  const dt = safeDate(responseDeadline);
  const isClosed = !!closingDate;
  const now = new Date();
  const overdue = !!dt && dt < now && !isClosed;

  const textColor = overdue ? COLORS.danger : isDarkMode ? "#fff" : "#111827";

  return (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      <span style={{ color: textColor, fontWeight: overdue ? 700 : 500 }}>
        {fmtDDMMYYYY(responseDeadline)}
      </span>
      {isClosed ? (
        <Badge bg="secondary">Cerrado</Badge>
      ) : overdue ? (
        <Badge bg="danger">Vencido</Badge>
      ) : (
        <Badge bg="success">En término</Badge>
      )}
    </div>
  );
};

const StatusPill = ({ text }) => {
  const t = String(text || "").toLowerCase();

  let bg = "secondary";
  if (t.includes("cerr")) bg = "dark";
  else if (t.includes("negoci")) bg = "primary";
  else if (t.includes("tram") || t.includes("curso")) bg = "info";
  else if (t.includes("venc")) bg = "danger";
  else if (t.includes("regist")) bg = "secondary";

  return (
    <Badge bg={bg} className="fw-semibold">
      {text || "—"}
    </Badge>
  );
};

const Truncated = ({ text, max = 92 }) => {
  const s = String(text || "");
  if (s.length <= max) return <span>{s}</span>;
  return <span title={s}>{s.slice(0, max).trim()}…</span>;
};

// ==========================
// Main
// ==========================
export default function LawyerWorkloadDashboard() {
  // Dark mode
  const [isDarkMode, setIsDarkMode] = useState(checkDarkMode());

  useEffect(() => {
    setIsDarkMode(checkDarkMode());

    const targetNode = document.querySelector(".app") || document.body;
    const observer = new MutationObserver(() => setIsDarkMode(checkDarkMode()));

    observer.observe(targetNode, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  // Parámetros
  const [lawyerId, setLawyerId] = useState("");
  const [from, setFrom] = useState(subMonthsISO(12));
  const [to, setTo] = useState(toISODate(new Date()));
  const [quickRange, setQuickRange] = useState("12m"); // "30d" | "90d" | "12m" | "custom"

  // Estado
  const [loading, setLoading] = useState(false);
  const [dto, setDto] = useState(null);

  // Export
  const [exporting, setExporting] = useState(false);

  // UX: filtro en tabla recientes
  const [recentFilter, setRecentFilter] = useState("");
  const [showOnlyOverdue, setShowOnlyOverdue] = useState(false);

  const isValidRange = useMemo(() => {
    if (!from || !to) return false;
    const f = safeDate(from);
    const t = safeDate(to);
    if (!f || !t) return false;
    return t.getTime() >= f.getTime();
  }, [from, to]);

  const canQuery = useMemo(
    () => !!lawyerId && isValidRange && !loading,
    [lawyerId, isValidRange, loading]
  );

  const applyQuickRange = (key) => {
    setQuickRange(key);

    if (key === "30d") {
      setFrom(subDaysISO(30));
      setTo(toISODate(new Date()));
      return;
    }
    if (key === "90d") {
      setFrom(subDaysISO(90));
      setTo(toISODate(new Date()));
      return;
    }
    // 12m
    setFrom(subMonthsISO(12));
    setTo(toISODate(new Date()));
  };

  const onManualFromChange = (v) => {
    setFrom(v);
    setQuickRange("custom");
  };

  const onManualToChange = (v) => {
    setTo(v);
    setQuickRange("custom");
  };

  const load = useCallback(async () => {
    if (!lawyerId) {
      Swal.fire("Atención", "Selecciona un abogado para consultar.", "warning");
      return;
    }
    if (!isValidRange) {
      Swal.fire("Atención", "El rango de fechas no es válido.", "warning");
      return;
    }

    try {
      setLoading(true);
      const res = await getLawyerWorkloadDashboard(lawyerId, from, to);
      if (!res?.success)
        throw new Error(res?.message || "No fue posible cargar el dashboard.");
      setDto(res.data);
    } catch (e) {
      Swal.fire(
        "Error",
        e?.message || "No se pudo cargar el dashboard",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [lawyerId, from, to, isValidRange]);

  const exportToPDF = useCallback(async () => {
    if (!lawyerId) {
      Swal.fire(
        "Atención",
        "Selecciona un abogado antes de exportar.",
        "warning"
      );
      return;
    }

    if (!from || !to) {
      Swal.fire("Atención", "Selecciona un rango de fechas válido.", "warning");
      return;
    }

    try {
      setExporting(true);

      // 1) Volver a consultar para garantizar consistencia con el backend
      const fresh = await getLawyerWorkloadDashboard(lawyerId, from, to);
      if (!fresh?.success) {
        throw new Error(
          fresh?.message || "No fue posible consultar la data para exportar."
        );
      }

      // 2) Clonar para evitar referencias reactivas
      const cloned = JSON.parse(JSON.stringify(fresh.data));

      // 3) Generar PDF con data fresca
      const blob = await pdf(
        <LawyerWorkloadDashboardPDF data={cloned} />
      ).toBlob();
      const url = URL.createObjectURL(blob);

      const safeName = (cloned?.lawyerName || "sin-nombre")
        .toLowerCase()
        .replaceAll(" ", "-")
        .replaceAll("/", "-");

      const fileName = `dashboard-abogado-${safeName}-${toISODate(
        new Date()
      )}.pdf`;

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      Swal.fire(
        "Error",
        e?.message || "No fue posible exportar el PDF.",
        "error"
      );
    } finally {
      setExporting(false);
    }
  }, [lawyerId, from, to]);

  // ==========================
  // Normalización + derivadas
  // ==========================
  const data = dto ?? {};
  const trendRows = Array.isArray(data.trendByMonth) ? data.trendByMonth : [];
  const statusRows = Array.isArray(data.statusDistributionNow)
    ? data.statusDistributionNow
    : [];
  const statusTotal = useMemo(
    () => statusRows.reduce((acc, x) => acc + (Number(x?.value) || 0), 0),
    [statusRows]
  );
  const typeRows = Array.isArray(data.caseTypeDistributionInRange)
    ? data.caseTypeDistributionInRange
    : [];
  const recentCases = Array.isArray(data.recentCases) ? data.recentCases : [];

  const derived = useMemo(() => {
    const totalEver = Number(data.totalAssignedEver || 0);
    const activeNow = Number(data.activeNow || 0);
    const closedEver = Number(data.closedEver || 0);
    const openedRange = Number(data.openedInRange || 0);
    const closedRange = Number(data.closedInRange || 0);
    const deadlineActive = Number(data.deadlineBasedActiveNow || 0);
    const overdueNow = Number(data.overdueNow || 0);

    const closeRateEver = totalEver > 0 ? (closedEver / totalEver) * 100 : 0;
    const closeRateRange =
      openedRange > 0 ? (closedRange / openedRange) * 100 : 0;
    const overduePct =
      deadlineActive > 0 ? (overdueNow / deadlineActive) * 100 : 0;

    return {
      totalEver,
      activeNow,
      closedEver,
      openedRange,
      closedRange,
      deadlineActive,
      overdueNow,
      closeRateEver,
      closeRateRange,
      overduePct,
    };
  }, [data]);

  const rangeLabel = useMemo(() => {
    const f = data.from ?? from;
    const t = data.to ?? to;
    return `${fmtDDMMYYYY(f)} - ${fmtDDMMYYYY(t)}`;
  }, [data.from, data.to, from, to]);

  const rangeDays = useMemo(() => {
    const f = data.from ?? from;
    const t = data.to ?? to;
    return daysDiff(f, t);
  }, [data.from, data.to, from, to]);

  // ==========================
  // Charts (dark aware)
  // ==========================
  const axisTickColor = isDarkMode
    ? "rgba(255,255,255,0.75)"
    : "rgba(0,0,0,0.65)";
  const gridColor = isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";

  const trendData = useMemo(
    () => ({
      labels: trendRows.map((r) => monthLabelES(r.period)),
      datasets: [
        {
          label: "Abiertos",
          data: trendRows.map((r) => r.opened || 0),
          tension: 0.25,
          borderWidth: 3,
          borderColor: COLORS.opened,
          backgroundColor: hexToRgba(COLORS.opened, 0.16),
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: COLORS.opened,
          fill: true,
        },
        {
          label: "Cerrados",
          data: trendRows.map((r) => r.closed || 0),
          tension: 0.25,
          borderWidth: 3,
          borderColor: COLORS.closed,
          backgroundColor: hexToRgba(COLORS.closed, 0.14),
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: COLORS.closed,
          fill: true,
        },
      ],
    }),
    [trendRows]
  );

  const trendOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
          labels: { color: axisTickColor },
        },
        tooltip: { mode: "index", intersect: false },
      },
      interaction: { mode: "index", intersect: false },
      scales: {
        y: {
          beginAtZero: true,
          ticks: { precision: 0, color: axisTickColor },
          grid: { color: gridColor },
        },
        x: {
          ticks: { maxRotation: 0, autoSkip: true, color: axisTickColor },
          grid: { color: gridColor },
        },
      },
    }),
    [axisTickColor, gridColor]
  );

  const statusDoughnut = useMemo(() => {
    const labels = statusRows.map((x) => x.key);
    const values = statusRows.map((x) => x.value || 0);

    return {
      labels,
      datasets: [
        {
          label: "Activos por estado",
          data: values,
          backgroundColor: labels.map(
            (_, i) => DONUT_COLORS[i % DONUT_COLORS.length]
          ),
          borderColor: isDarkMode ? "rgba(255,255,255,0.10)" : "#ffffff",
          borderWidth: 2,
          hoverOffset: 8,
        },
      ],
    };
  }, [statusRows, isDarkMode]);

  const doughnutOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "70%",
      plugins: {
        legend: {
          position: "bottom",
          labels: { color: axisTickColor },
        },

        tooltip: {
          callbacks: {
            label: (ctx) => {
              const v = Number(ctx.raw) || 0;
              const pct = statusTotal > 0 ? (v / statusTotal) * 100 : 0;
              return ` ${ctx.label}: ${v} (${pct.toFixed(0)}%)`;
            },
          },
        },

        // Texto en el centro
        doughnutCenterTextPlugin: {
          textTop: statusTotal,
          textBottom: "Activos",
          colorTop: isDarkMode ? "#ffffff" : "#111827",
          colorBottom: isDarkMode
            ? "rgba(255,255,255,0.70)"
            : "rgba(0,0,0,0.55)",
        },
      },
    }),
    [axisTickColor, isDarkMode, statusTotal]
  );

  const typeBar = useMemo(() => {
    const labels = typeRows.map((x) => x.key);
    const values = typeRows.map((x) => x.value || 0);

    return {
      labels,
      datasets: [
        {
          label: "Abiertos en rango",
          data: values,
          backgroundColor: hexToRgba(COLORS.bar, 0.78),
          borderColor: COLORS.bar,
          borderWidth: 1,
          borderRadius: 10,
          maxBarThickness: 46,
        },
      ],
    };
  }, [typeRows]);

  const barOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        y: {
          beginAtZero: true,
          ticks: { precision: 0, color: axisTickColor },
          grid: { color: gridColor },
        },
        x: {
          ticks: { autoSkip: false, color: axisTickColor },
          grid: { color: gridColor },
        },
      },
    }),
    [axisTickColor, gridColor]
  );

  // ==========================
  // Tabla recientes (UX)
  // ==========================
  const recentFiltered = useMemo(() => {
    const term = String(recentFilter || "")
      .trim()
      .toLowerCase();
    const now = new Date();

    return recentCases.filter((c) => {
      if (!c) return false;

      const txt = `${c.internalCode || ""} ${c.title || ""} ${
        c.caseTypeName || ""
      } ${c.caseStatusName || ""}`.toLowerCase();

      const matches = !term || txt.includes(term);

      const isClosed = !!c.closingDate;
      const deadlineDt = safeDate(c.responseDeadline);
      const overdue =
        !!c.isDeadlineBasedCase &&
        !!deadlineDt &&
        deadlineDt < now &&
        !isClosed;

      const matchesOverdue = !showOnlyOverdue || overdue;

      return matches && matchesOverdue;
    });
  }, [recentCases, recentFilter, showOnlyOverdue]);

  const summaryRecent = useMemo(() => {
    const now = new Date();
    const overdueCount = recentCases.reduce((acc, c) => {
      const isClosed = !!c?.closingDate;
      const d = safeDate(c?.responseDeadline);
      const overdue = !!c?.isDeadlineBasedCase && !!d && d < now && !isClosed;
      return acc + (overdue ? 1 : 0);
    }, 0);

    return { total: recentCases.length, overdueCount };
  }, [recentCases]);

  const cardBg = isDarkMode ? "#151522" : "#ffffff";
  const cardBorder = isDarkMode ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)";
  const cardShadow = isDarkMode
    ? "0 14px 34px rgba(0,0,0,0.45)"
    : "0 12px 30px rgba(0,0,0,0.10)";
  const mutedText = isDarkMode ? "rgba(255,255,255,0.70)" : "rgba(0,0,0,0.55)";

  const tableClass = useMemo(() => {
    // Nota: mantenemos hover y lo controlamos con CSS para evitar “opacar” texto en dark.
    return `lwd-recent-table ${
      isDarkMode ? "table table-dark" : "table"
    } table-hover align-middle mb-0`;
  }, [isDarkMode]);

  return (
    <div className="container-fluid mt-4 px-4 w-100">
      <Card
        className="p-4"
        style={{
          background: cardBg,
          borderColor: cardBorder,
          boxShadow: cardShadow,
          borderRadius: 16,
        }}
      >
        {/* ==========================
            Parámetros (panel superior)
        ========================== */}
        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
          <div>
            <h4
              className="mb-1"
              style={{ color: isDarkMode ? "#fff" : "#111827" }}
            >
              Dashboard de carga de trabajo
            </h4>
            <div style={{ color: mutedText }}>
              Selecciona abogado y rango. Luego presiona{" "}
              <strong>Consultar</strong>.
            </div>

            {dto?.lawyerName ? (
              <div className="small mt-2" style={{ color: mutedText }}>
                Abogado:{" "}
                <strong style={{ color: isDarkMode ? "#fff" : "#111827" }}>
                  {dto.lawyerName}
                </strong>{" "}
                <Badge bg="secondary" className="ms-2">
                  {rangeLabel}
                </Badge>{" "}
                {rangeDays != null ? (
                  <Badge bg="light" text="dark" className="ms-2">
                    {rangeDays} días
                  </Badge>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="d-flex gap-3 flex-wrap align-items-end">
            <Form.Group style={{ minWidth: 340 }}>
              <Form.Label className="mb-1" style={{ color: mutedText }}>
                Abogado
              </Form.Label>
              <SelectLawyer value={lawyerId} onChange={setLawyerId} />
            </Form.Group>

            <Form.Group>
              <Form.Label className="mb-1" style={{ color: mutedText }}>
                Desde
              </Form.Label>
              <Form.Control
                type="date"
                value={from}
                onChange={(e) => onManualFromChange(e.target.value)}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="mb-1" style={{ color: mutedText }}>
                Hasta
              </Form.Label>
              <Form.Control
                type="date"
                value={to}
                onChange={(e) => onManualToChange(e.target.value)}
              />
            </Form.Group>

            <div className="d-flex gap-2 align-items-end flex-wrap">
              <QuickRangeButton
                active={quickRange === "30d"}
                onClick={() => applyQuickRange("30d")}
              >
                30 días
              </QuickRangeButton>

              <QuickRangeButton
                active={quickRange === "90d"}
                onClick={() => applyQuickRange("90d")}
              >
                90 días
              </QuickRangeButton>

              <QuickRangeButton
                active={quickRange === "12m"}
                onClick={() => applyQuickRange("12m")}
              >
                12 meses
              </QuickRangeButton>

              <Button onClick={load} disabled={!canQuery}>
                {loading ? "Cargando..." : "Consultar"}
              </Button>

              <Button
                variant="outline-primary"
                onClick={exportToPDF}
                disabled={!dto || exporting}
              >
                {exporting ? "Exportando..." : "Exportar PDF"}
              </Button>
            </div>
          </div>
        </div>

        {!isValidRange && (
          <div className="mt-3">
            <Badge bg="warning" text="dark">
              El rango de fechas no es válido (Hasta debe ser mayor o igual a
              Desde).
            </Badge>
          </div>
        )}

        {loading && (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" />
          </div>
        )}

        {/* ==========================
            Resultados
        ========================== */}
        {!loading && dto && (
          <>
            {/* KPIs */}
            <Row className="mt-4 g-3">
              <Col md={3}>
                <KPI
                  title="Histórico asignados"
                  value={derived.totalEver}
                  hint="Total de casos históricos."
                  accent={COLORS.opened}
                  iconClass="bi bi-collection"
                  isDarkMode={isDarkMode}
                />
              </Col>

              <Col md={3}>
                <KPI
                  title="Activos hoy"
                  value={derived.activeNow}
                  hint="Activos y sin cierre."
                  badge={derived.activeNow > 0 ? "En curso" : "OK"}
                  badgeVariant={derived.activeNow > 0 ? "primary" : "success"}
                  accent={COLORS.info}
                  iconClass="bi bi-lightning-charge"
                  isDarkMode={isDarkMode}
                />
              </Col>

              <Col md={3}>
                <KPI
                  title="Cerrados históricos"
                  value={derived.closedEver}
                  hint={`Tasa: ${derived.closeRateEver.toFixed(0)}%`}
                  accent={COLORS.closed}
                  iconClass="bi bi-check2-circle"
                  isDarkMode={isDarkMode}
                />
              </Col>

              <Col md={3}>
                <KPI
                  title="Vencidos hoy"
                  value={derived.overdueNow}
                  danger={derived.overdueNow > 0}
                  hint={`Activos con fecha límite: ${derived.deadlineActive}`}
                  badge={derived.overdueNow > 0 ? "Atención" : "OK"}
                  badgeVariant={derived.overdueNow > 0 ? "danger" : "success"}
                  accent={COLORS.danger}
                  iconClass="bi bi-exclamation-triangle"
                  isDarkMode={isDarkMode}
                />
              </Col>

              <Col md={3}>
                <KPI
                  title="Abiertos en rango"
                  value={derived.openedRange}
                  hint="Creados en el rango."
                  accent={COLORS.bar}
                  iconClass="bi bi-folder-plus"
                  isDarkMode={isDarkMode}
                />
              </Col>

              <Col md={3}>
                <KPI
                  title="Cerrados en rango"
                  value={derived.closedRange}
                  hint={`Tasa: ${derived.closeRateRange.toFixed(0)}%`}
                  accent={COLORS.closed}
                  iconClass="bi bi-folder-check"
                  isDarkMode={isDarkMode}
                />
              </Col>

              <Col md={6}>
                <Card
                  className="p-3 h-100"
                  style={{
                    background: isDarkMode ? "#1e1e2f" : "#ffffff",
                    borderRadius: 14,
                    border: `1px solid ${
                      isDarkMode ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)"
                    }`,
                    boxShadow: isDarkMode
                      ? "0 12px 28px rgba(0,0,0,0.35)"
                      : "0 10px 24px rgba(0,0,0,0.10)",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div style={{ color: mutedText }}>
                      Riesgo por vencimientos
                    </div>
                    <Badge bg={derived.overdueNow > 0 ? "danger" : "success"}>
                      {derived.overdueNow > 0 ? "Riesgo" : "Controlado"}
                    </Badge>
                  </div>

                  <div className="mt-2">
                    <div
                      className="d-flex justify-content-between small"
                      style={{ color: mutedText }}
                    >
                      <span>Vencidos / Activos con fecha límite</span>
                      <span>
                        {derived.overdueNow}/{derived.deadlineActive}
                      </span>
                    </div>

                    <ProgressBar
                      className="mt-2"
                      now={clampPct(derived.overduePct)}
                      variant={derived.overdueNow > 0 ? "danger" : "success"}
                      label={`${Math.round(derived.overduePct)}%`}
                    />
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Charts */}
            <Row className="mt-4 g-3">
              <Col lg={8}>
                <Card
                  className="p-3 h-100"
                  style={{
                    background: isDarkMode ? "#1e1e2f" : "#ffffff",
                    borderRadius: 14,
                    border: `1px solid ${
                      isDarkMode ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)"
                    }`,
                    boxShadow: isDarkMode
                      ? "0 12px 28px rgba(0,0,0,0.35)"
                      : "0 10px 24px rgba(0,0,0,0.10)",
                  }}
                >
                  <div
                    className="fw-bold mb-2"
                    style={{ color: isDarkMode ? "#fff" : "#111827" }}
                  >
                    Tendencia mensual (Abiertos vs Cerrados)
                  </div>

                  {trendRows.every(
                    (x) => (x?.opened || 0) === 0 && (x?.closed || 0) === 0
                  ) ? (
                    <EmptyState
                      title="Sin actividad en el rango"
                      subtitle="No hay aperturas ni cierres registrados para este periodo."
                      isDarkMode={isDarkMode}
                    />
                  ) : (
                    <div style={{ height: 320 }}>
                      <Line data={trendData} options={trendOptions} />
                    </div>
                  )}
                </Card>
              </Col>

              <Col lg={4}>
                <Card
                  className="p-3 h-100"
                  style={{
                    background: isDarkMode ? "#1e1e2f" : "#ffffff",
                    borderRadius: 14,
                    border: `1px solid ${
                      isDarkMode ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)"
                    }`,
                    boxShadow: isDarkMode
                      ? "0 12px 28px rgba(0,0,0,0.35)"
                      : "0 10px 24px rgba(0,0,0,0.10)",
                  }}
                >
                  <div
                    className="fw-bold mb-2"
                    style={{ color: isDarkMode ? "#fff" : "#111827" }}
                  >
                    Distribución por estado (activos)
                  </div>

                  {statusRows.length === 0 ? (
                    <EmptyState
                      title="Sin datos"
                      subtitle="No hay estados activos para mostrar."
                      isDarkMode={isDarkMode}
                    />
                  ) : (
                    <div style={{ height: 320 }}>
                      <Doughnut
                        data={statusDoughnut}
                        options={doughnutOptions}
                      />
                    </div>
                  )}
                </Card>
              </Col>

              <Col lg={12}>
                <Card
                  className="p-3"
                  style={{
                    background: isDarkMode ? "#1e1e2f" : "#ffffff",
                    borderRadius: 14,
                    border: `1px solid ${
                      isDarkMode ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)"
                    }`,
                    boxShadow: isDarkMode
                      ? "0 12px 28px rgba(0,0,0,0.35)"
                      : "0 10px 24px rgba(0,0,0,0.10)",
                  }}
                >
                  <div
                    className="fw-bold mb-2"
                    style={{ color: isDarkMode ? "#fff" : "#111827" }}
                  >
                    Top tipos de caso (abiertos en rango)
                  </div>

                  {typeRows.length === 0 ? (
                    <EmptyState
                      title="Sin datos"
                      subtitle="No hay tipos de caso en el rango seleccionado."
                      isDarkMode={isDarkMode}
                    />
                  ) : (
                    <div style={{ height: 320 }}>
                      <Bar data={typeBar} options={barOptions} />
                    </div>
                  )}
                </Card>
              </Col>
            </Row>

            {/* Casos recientes */}
            <Row className="mt-4 g-3">
              <Col lg={12}>
                <Card
                  className="p-3"
                  style={{
                    background: isDarkMode ? "#1e1e2f" : "#ffffff",
                    borderRadius: 14,
                    border: `1px solid ${
                      isDarkMode ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)"
                    }`,
                    boxShadow: isDarkMode
                      ? "0 12px 28px rgba(0,0,0,0.35)"
                      : "0 10px 24px rgba(0,0,0,0.10)",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                    <div>
                      <div
                        className="fw-bold"
                        style={{ color: isDarkMode ? "#fff" : "#111827" }}
                      >
                        Casos recientes
                      </div>
                      <div className="small" style={{ color: mutedText }}>
                        Últimos movimientos del abogado (top 10).
                        {summaryRecent.total > 0 ? (
                          <>
                            <Badge bg="light" text="dark" className="ms-2">
                              {summaryRecent.total} items
                            </Badge>
                            {summaryRecent.overdueCount > 0 ? (
                              <Badge bg="danger" className="ms-2">
                                {summaryRecent.overdueCount} vencidos
                              </Badge>
                            ) : (
                              <Badge bg="success" className="ms-2">
                                0 vencidos
                              </Badge>
                            )}
                          </>
                        ) : null}
                      </div>
                    </div>

                    <div className="d-flex gap-2 align-items-end flex-wrap">
                      <Form.Check
                        type="switch"
                        id="only-overdue"
                        label="Solo vencidos"
                        checked={showOnlyOverdue}
                        onChange={(e) => setShowOnlyOverdue(e.target.checked)}
                      />

                      <InputGroup style={{ minWidth: 320 }}>
                        <InputGroup.Text>Buscar</InputGroup.Text>
                        <Form.Control
                          value={recentFilter}
                          onChange={(e) => setRecentFilter(e.target.value)}
                          placeholder="Código, título, tipo, estado..."
                        />
                        {recentFilter ? (
                          <Button
                            variant="outline-secondary"
                            onClick={() => setRecentFilter("")}
                          >
                            Limpiar
                          </Button>
                        ) : null}
                      </InputGroup>
                    </div>
                  </div>

                  <div className="table-responsive mt-3">
                    <Table hover className={tableClass}>
                      <thead className={isDarkMode ? "" : "table-light"}>
                        <tr>
                          <th style={{ width: 130 }}>Código</th>
                          <th>Título</th>
                          <th style={{ width: 260 }}>Tipo</th>
                          <th style={{ width: 170 }}>Estado</th>
                          <th style={{ width: 130 }}>Creado</th>
                          <th style={{ width: 130 }}>Radicado</th>
                          <th style={{ width: 130 }}>Cierre</th>
                          <th style={{ width: 240 }}>Fecha límite</th>
                        </tr>
                      </thead>

                      <tbody>
                        {recentFiltered.length === 0 ? (
                          <tr>
                            <td
                              colSpan={8}
                              className="text-center py-4"
                              style={{ color: mutedText }}
                            >
                              No hay casos para mostrar con los filtros
                              actuales.
                            </td>
                          </tr>
                        ) : (
                          recentFiltered.map((c) => {
                            const now = new Date();
                            const isClosed = !!c.closingDate;
                            const deadlineDt = safeDate(c.responseDeadline);
                            const overdue =
                              !!c.isDeadlineBasedCase &&
                              !!deadlineDt &&
                              deadlineDt < now &&
                              !isClosed;

                            return (
                              <tr
                                key={c.id}
                                className={overdue ? "table-danger" : ""}
                              >
                                <td className="fw-semibold">
                                  {c.internalCode || "—"}
                                </td>

                                <td>
                                  <div className="fw-semibold">
                                    <Truncated text={c.title} max={110} />
                                  </div>
                                  <div
                                    className="small"
                                    style={{ color: mutedText }}
                                  ></div>
                                </td>

                                <td>
                                  <Truncated text={c.caseTypeName} max={60} />
                                </td>

                                <td>
                                  <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <StatusPill text={c.caseStatusName} />
                                    {overdue ? (
                                      <Badge bg="danger">Prioridad</Badge>
                                    ) : null}
                                  </div>
                                </td>

                                <td>{fmtDDMMYYYY(c.createdAt)}</td>
                                <td>{fmtDDMMYYYY(c.filingDate)}</td>
                                <td>{fmtDDMMYYYY(c.closingDate)}</td>

                                <td>
                                  <DeadlineBadge
                                    isDeadlineBasedCase={c.isDeadlineBasedCase}
                                    responseDeadline={c.responseDeadline}
                                    closingDate={c.closingDate}
                                    isDarkMode={isDarkMode}
                                  />
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Card>
    </div>
  );
}
